// "Search with image" — the Browse-only reverse-image entry point, beside the search box.
//
// Picks a file, downscales it, embeds it ONCE, and hands the resulting vector to the store.
// From there `navigation.ts` folds it into every `search.load` body, so paging re-ranks
// against the same vector without re-embedding, and the sidebar section renders the chip.
//
// The embedding goes through `search.vector`, which is the endpoint that already owns image
// embedding for /labs/search. `k: 1` because its ranked results are thrown away here — only
// `returnVec` is wanted, and that flag exists for exactly this "give me the query vector"
// use (the labs semantic mixer is the other caller).
import { useRef, useState } from "preact/hooks";
import { api } from "../../api";
import { getAction } from "../../actionRegistry";
import { setSearchActive } from "../../globalState";
import { toastError } from "../../ui/messages";
import { downscaleForEmbedding, vecToB64 } from "./downscale";
import { setSearchImage } from "./store";

export function SearchImageButton() {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  async function run(file: File): Promise<void> {
    if (!file.type.startsWith("image/")) {
      toastError("That is not an image file");
      return;
    }
    setBusy(true);
    try {
      const { b64, preview } = await downscaleForEmbedding(file);
      const res = await api.search.vector({ imageB64: b64, k: 1, returnVec: true });
      if (!res.queryVec) throw new Error("The image could not be read");
      setSearchImage({ name: file.name, preview, vecB64: vecToB64(res.queryVec) });
      // A search with an image but an empty box is still a search. `search()` only runs its
      // empty-box branch when a search is already active, so say so before asking.
      setSearchActive(1);
      getAction("searchtimed")?.();
    } catch (err: unknown) {
      toastError(err instanceof Error ? err.message : "Image search failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <span class="search-image">
      {/* The label does NOT change once an image is attached: the button's job is the same
          either way — pick a file — and the attached image is already reported by the
          sidebar chip, which is also where removing it lives.

          A real file input, kept out of the layout and clicked through the ref — a bare
          `document.createElement("input")` (what the checker modal does) is fine for a
          one-shot, but this one is re-used and needs its value cleared so picking the SAME
          file twice still fires `change`. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        class="search-image-file"
        onChange={(e) => {
          const input = e.currentTarget;
          const file = input.files?.[0];
          input.value = "";
          if (file) void run(file);
        }}
      />
      <span
        class={`button medium search-image-button${busy ? " search-image-busy" : ""}`}
        onClick={() => {
          if (!busy) inputRef.current?.click();
        }}
      >
        <i class={`fas fa-${busy ? "spinner fa-spin" : "image"}`} />{" "}
        {busy ? "Reading…" : "Image search"}
      </span>
    </span>
  );
}
